/**
 * The org chart.
 *
 * The API sends a **flat list** of nodes, each naming its manager, and the tree
 * is assembled here. That split is deliberate: a chart, a picker and a filter
 * all want different shapes from the same data, and an endpoint that returns
 * one nested structure forces two of the three to un-nest it.
 *
 * Assembled in one pass with a map, so it is linear in the number of people
 * rather than quadratic — and it tolerates the two things real data does:
 * several roots, and a manager who is not in the list because they have left.
 */

import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ChevronRight, Users } from 'lucide-react';
import {
  Avatar,
  Card,
  CardBody,
  EmptyState,
  ErrorState,
  Input,
  LoadingState,
  PageHeader,
  api,
  cn,
} from '@connect/web-shared';

export interface Node {
  public_id: string;
  full_name: string;
  job_title: string | null;
  department_name: string | null;
  manager_public_id: string | null;
}

export interface TreeNode extends Node {
  reports: TreeNode[];
}

/**
 * Flat list to forest.
 *
 * Anybody whose manager is absent from the list becomes a root rather than
 * disappearing — a departed manager must not take their whole team off the
 * chart.
 */
export function toForest(nodes: Node[]): TreeNode[] {
  const byId = new Map<string, TreeNode>(
    nodes.map((node) => [node.public_id, { ...node, reports: [] }]),
  );
  const roots: TreeNode[] = [];

  for (const node of byId.values()) {
    const parent =
      node.manager_public_id !== null ? byId.get(node.manager_public_id) : undefined;
    if (parent && parent.public_id !== node.public_id) {
      parent.reports.push(node);
    } else {
      roots.push(node);
    }
  }

  const sort = (list: TreeNode[]) => {
    list.sort((a, b) => a.full_name.localeCompare(b.full_name));
    list.forEach((child) => sort(child.reports));
  };
  sort(roots);
  return roots;
}

function Branch({ node, depth }: { node: TreeNode; depth: number }) {
  const [open, setOpen] = useState(depth < 2);
  const hasReports = node.reports.length > 0;

  return (
    <li>
      <div
        className="flex items-center gap-2 rounded-control px-2 py-1.5 hover:bg-surface-2"
        style={{ paddingLeft: `${depth * 1.25 + 0.5}rem` }}
      >
        <button
          type="button"
          onClick={() => setOpen((was) => !was)}
          className={cn(
            'shrink-0 rounded p-0.5 text-faint transition',
            hasReports ? 'hover:bg-surface-3 hover:text-body' : 'invisible',
          )}
          aria-label={open ? `Collapse ${node.full_name}` : `Expand ${node.full_name}`}
          aria-expanded={hasReports ? open : undefined}
        >
          <ChevronRight className={cn('size-3.5 transition-transform', open && 'rotate-90')} />
        </button>
        <Avatar name={node.full_name} size="xs" />
        <span className="min-w-0 flex-1">
          <span className="block truncate text-sm text-body">{node.full_name}</span>
          <span className="block truncate text-2xs text-faint">
            {[node.job_title, node.department_name].filter(Boolean).join(' · ') || '—'}
          </span>
        </span>
        {hasReports ? (
          <span className="shrink-0 text-2xs text-faint">
            {node.reports.length} {node.reports.length === 1 ? 'report' : 'reports'}
          </span>
        ) : null}
      </div>
      {hasReports && open ? (
        <ul>
          {node.reports.map((child) => (
            <Branch key={child.public_id} node={child} depth={depth + 1} />
          ))}
        </ul>
      ) : null}
    </li>
  );
}

export function OrgChartPage() {
  const [term, setTerm] = useState('');

  const chart = useQuery({
    queryKey: ['hr', 'org-chart'],
    queryFn: () => api.get<Node[]>('/hr/org-chart'),
  });

  const forest = useMemo(() => toForest(chart.data ?? []), [chart.data]);

  /**
   * Searching flattens the tree on purpose.
   *
   * Filtering a hierarchy leaves orphaned branches and half-visible chains; a
   * flat list of matches is what somebody typing a name actually wants.
   */
  const matches = useMemo(() => {
    const needle = term.trim().toLowerCase();
    if (!needle) return null;
    return (chart.data ?? []).filter(
      (node) =>
        node.full_name.toLowerCase().includes(needle) ||
        (node.job_title ?? '').toLowerCase().includes(needle) ||
        (node.department_name ?? '').toLowerCase().includes(needle),
    );
  }, [chart.data, term]);

  if (chart.isLoading) return <LoadingState label="Building the chart" />;
  if (chart.isError) return <ErrorState onRetry={() => void chart.refetch()} />;

  return (
    <div className="space-y-4">
      <PageHeader
        title="Org chart"
        description="Who reports to whom. Assembled from each person's manager."
      />

      <Card>
        <CardBody className="space-y-3">
          <Input
            placeholder="Find somebody"
            value={term}
            onChange={(event) => setTerm(event.target.value)}
            containerClassName="max-w-sm"
          />

          {(chart.data ?? []).length === 0 ? (
            <EmptyState
              title="Nobody on the chart"
              description="People appear here once they have a manager set, or as a root if they have none."
              icon={<Users className="size-5" />}
            />
          ) : matches !== null ? (
            matches.length === 0 ? (
              <p className="py-6 text-center text-sm text-muted">Nobody matches that.</p>
            ) : (
              <ul className="divide-y divide-line">
                {matches.map((node) => (
                  <li key={node.public_id} className="flex items-center gap-2 py-2">
                    <Avatar name={node.full_name} size="xs" />
                    <span className="min-w-0">
                      <span className="block truncate text-sm text-body">{node.full_name}</span>
                      <span className="block truncate text-2xs text-faint">
                        {[node.job_title, node.department_name].filter(Boolean).join(' · ') || '—'}
                      </span>
                    </span>
                  </li>
                ))}
              </ul>
            )
          ) : (
            <ul>
              {forest.map((node) => (
                <Branch key={node.public_id} node={node} depth={0} />
              ))}
            </ul>
          )}
        </CardBody>
      </Card>
    </div>
  );
}
